import { useEffect, useMemo, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { api, API_BASE, getToken } from '../lib/api';

/**
 * "Comunicados" (V92) — correo masivo del tenant a sus contactos.
 *
 * Composer LIGERO (decisión aprobada): contenteditable con toolbar mínima
 * (negritas, cursiva, subrayado, enlace, lista) — sin librería de rich-text.
 * El backend SANEA el HTML con allowlist estricta; el shell del correo (logo
 * del tenant + footer legal + botón de baja) lo arma el sender — aquí solo se
 * edita el cuerpo. "Seleccionar todos" = todos los ELEGIBLES (activos + email
 * + sin baja); los dados de baja se muestran en gris (LFPDPPP: no se les
 * puede enviar y la UI lo hace visible en vez de esconderlos).
 */

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

const STATUS_META: Record<string, { label: string; cls: string }> = {
  DRAFT:     { label: 'Borrador',   cls: 'bg-slate-100 text-slate-600' },
  SCHEDULED: { label: 'Programado', cls: 'bg-amber-100 text-amber-700' },
  SENDING:   { label: 'Enviando…',  cls: 'bg-sky-100 text-sky-700' },
  SENT:      { label: 'Enviado',    cls: 'bg-emerald-100 text-emerald-700' },
  CANCELLED: { label: 'Cancelado',  cls: 'bg-rose-100 text-rose-600' },
};

interface CommRow {
  id: string; subject: string; status: string;
  scheduledAt: string | null; sentAt: string | null;
  recipientsTotal: number; recipientsSent: number;
  recipientsFailed: number; recipientsSuppressed: number;
  createdAt: string;
}
interface EligibleContact { id: string; name: string; email: string }

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  }) : '—';

/**
 * Re-suscripción por STAFF (la baja del cliente es sagrada — LFPDPPP): solo
 * procede con consentimiento explícito del cliente y el backend deja rastro
 * en audit_logs. La advertencia va en el confirm, no en letra chica.
 */
async function resubscribe(email: string): Promise<boolean> {
  const ok = window.confirm(
    `⚠️ Re-suscribir ${email}\n\n` +
    'Solo re-suscribe si el cliente te lo pidió expresamente. ' +
    'Hacerlo sin su consentimiento es ilegal (LFPDPPP).\n\n' +
    'La acción queda registrada en la bitácora con tu usuario. ' +
    '¿Confirmas que el cliente lo solicitó?',
  );
  if (!ok) return false;
  await api(`/communications/suppressions/${encodeURIComponent(email)}`, { method: 'DELETE' });
  return true;
}

export function ComunicadosView({ autoCompose = false, onAutoComposeConsumed }: {
  autoCompose?: boolean; onAutoComposeConsumed?: () => void;
}) {
  const [rows, setRows] = useState<CommRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState<CommRow | 'new' | null>(autoCompose ? 'new' : null);
  // "Reenviar" abre el composer directo en el paso 2 (el contenido ya está clonado).
  const [composerStep, setComposerStep] = useState<1 | 2>(1);
  // One-shot: el flag viene del botón "Enviar comunicado" de Contactos.
  useEffect(() => { if (autoCompose) onAutoComposeConsumed?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [detailId, setDetailId] = useState<string | null>(null);
  const [suppressionsOpen, setSuppressionsOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await api('/communications')) as CommRow[]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id: string) => {
    if (!window.confirm('¿Cancelar este comunicado programado?')) return;
    try { await api(`/communications/${id}/cancel`, { method: 'POST' }); load(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); }
  };
  const removeDraft = async (id: string) => {
    if (!window.confirm('¿Borrar este borrador?')) return;
    try { await api(`/communications/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); }
  };

  /** "Reenviar": clona asunto + cuerpo (con imágenes — viven por URL en el
   *  bucket, el clon las referencia tal cual) a un NUEVO borrador y abre el
   *  composer directo en destinatarios (sobre elegibles ACTUALES: las bajas
   *  posteriores al envío original quedan fuera solas). */
  const resend = async (r: CommRow) => {
    setErr(null);
    try {
      const d: any = await api(`/communications/${r.id}`);
      const created: any = await api('/communications', {
        method: 'POST',
        body: { subject: d.subject, bodyHtml: d.bodyHtml },
      });
      setComposerStep(2);
      setComposer({ ...r, id: created.id, status: 'DRAFT' });
      load();
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo duplicar'); }
  };

  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Comunicados</h2>
          <p className="text-sm text-slate-500">
            Correo a tus contactos: novedades, promociones, avisos. Cada correo lleva
            link de baja; los correos de citas/reservas no se ven afectados por las bajas.
          </p>
        </div>
        <div className="flex gap-2">
          <button className={btnGhost} onClick={() => setSuppressionsOpen(true)}>Bajas</button>
          <button className={btnPrimary} onClick={() => { setComposerStep(1); setComposer('new'); }}>
            <span className="inline-flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Nuevo comunicado</span>
          </button>
        </div>
      </div>

      {err && <p className="alert-over mb-3 rounded-xl px-3 py-2 text-xs">{err}</p>}

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">Asunto</th><th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Destinatarios</th><th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  Sin comunicados todavía. Crea el primero con "Nuevo comunicado".
                </td></tr>
              )}
              {rows.map((r) => {
                const s = STATUS_META[r.status] ?? { label: r.status, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-2 font-medium text-slate-800">
                      <button className="hover:underline" onClick={() => setDetailId(r.id)}>{r.subject}</button>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {r.status === 'DRAFT' ? '—' : (
                        <>
                          {r.recipientsSent}/{r.recipientsTotal}
                          {r.recipientsFailed > 0 && <span className="ml-1 text-rose-600">({r.recipientsFailed} fallidos)</span>}
                          {r.recipientsSuppressed > 0 && <span className="ml-1 text-slate-400">({r.recipientsSuppressed} excluidos por baja)</span>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-500">
                      {r.status === 'SENT' ? fmt(r.sentAt) : r.status === 'SCHEDULED' ? `Sale ${fmt(r.scheduledAt)}` : fmt(r.createdAt)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        {r.status === 'DRAFT' && (
                          <>
                            <button className={btnGhost} onClick={() => { setComposerStep(1); setComposer(r); }}>Editar / Enviar</button>
                            <button className="rounded-xl px-2 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => removeDraft(r.id)}>Borrar</button>
                          </>
                        )}
                        {r.status === 'SCHEDULED' && (
                          <button className={btnGhost} onClick={() => cancel(r.id)}>Cancelar</button>
                        )}
                        {(r.status === 'SENT' || r.status === 'CANCELLED') && (
                          <button className={btnGhost} onClick={() => resend(r)}>Reenviar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {composer && (
        <ComposerModal
          draft={composer === 'new' ? null : composer}
          initialStep={composer === 'new' ? 1 : composerStep}
          onClose={() => setComposer(null)}
          onDone={() => { setComposer(null); load(); }}
        />
      )}
      {detailId && <DetailModal id={detailId} onClose={() => setDetailId(null)} />}
      {suppressionsOpen && <SuppressionsModal onClose={() => setSuppressionsOpen(false)} />}
    </div>
  );
}

// ===========================================================================
// Composer (paso 1: contenido · paso 2: destinatarios + programación)
// ===========================================================================
function ComposerModal({ draft, initialStep = 1, onClose, onDone }: {
  draft: CommRow | null; initialStep?: 1 | 2; onClose: () => void; onDone: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(initialStep);
  const [subject, setSubject] = useState(draft?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState('');
  const [draftId, setDraftId] = useState<string | null>(draft?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Si venimos de "Editar", cargar el cuerpo persistido (saneado).
  useEffect(() => {
    if (!draft) return;
    api(`/communications/${draft.id}`).then((d: any) => setBodyHtml(d.bodyHtml ?? ''))
      .catch(() => {});
  }, [draft]);

  const saveDraft = async (): Promise<string> => {
    const body = { subject: subject.trim(), bodyHtml };
    if (draftId) {
      await api(`/communications/${draftId}`, { method: 'PATCH', body });
      return draftId;
    }
    const created: any = await api('/communications', { method: 'POST', body });
    setDraftId(created.id);
    return created.id;
  };

  const toStep2 = async () => {
    setErr(null);
    if (!subject.trim()) { setErr('Escribe un asunto.'); return; }
    if (!bodyHtml.replace(/<[^>]+>/g, '').trim()) { setErr('El cuerpo está vacío.'); return; }
    setBusy(true);
    try { await saveDraft(); setStep(2); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo guardar'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">
            {step === 1 ? (draftId ? 'Editar comunicado' : 'Nuevo comunicado') : 'Destinatarios y envío'}
          </h3>
          <button className="text-slate-400 hover:text-slate-600" onClick={onClose}>✕</button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs text-slate-600">Asunto</span>
              <input className="input" value={subject} maxLength={200}
                onChange={(e) => setSubject(e.target.value)} placeholder="Ej. Horarios especiales de agosto" />
            </label>
            <div>
              <span className="mb-1.5 block text-xs text-slate-600">Mensaje</span>
              <RichBody value={bodyHtml} onChange={setBodyHtml} />
              <p className="mt-1 text-[11px] text-slate-400">
                Tu logo y nombre van en el encabezado automáticamente; el pie con el link de
                baja (obligatorio) se agrega al enviar.
              </p>
            </div>
            {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
            <div className="flex justify-end gap-2">
              <button className={btnGhost} onClick={onClose}>Cancelar</button>
              <button className={btnPrimary} disabled={busy} onClick={toStep2}>
                {busy ? 'Guardando…' : 'Guardar y elegir destinatarios →'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && draftId && (
          <RecipientsStep
            draftId={draftId}
            onBack={() => setStep(1)}
            onDone={onDone}
          />
        )}
      </div>
    </div>
  );
}

/** Toolbar mínima sobre contenteditable (sin dependencias). El HTML resultante
 *  lo sanea el backend con allowlist — aquí solo edición. Imágenes: botón o
 *  PEGAR directo (Ctrl+V); se suben al bucket comm-images y se insertan por
 *  URL (los correos no soportan data: URIs — Gmail las bloquea). */
function RichBody({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgErr, setImgErr] = useState<string | null>(null);
  // Solo hidratar cuando el valor llega de fuera (editar borrador) y el editor
  // está vacío — no en cada tecleo (rompería el caret).
  useEffect(() => {
    if (ref.current && value && ref.current.innerHTML === '') ref.current.innerHTML = value;
  }, [value]);

  const exec = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML ?? '');
  };
  const link = () => {
    const url = window.prompt('URL del enlace (https://…):');
    if (url && /^https?:\/\//.test(url)) exec('createLink', url);
  };

  const uploadImage = async (file: File) => {
    setImgErr(null);
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      setImgErr('Solo PNG o JPG.'); return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setImgErr('Máx 2MB por imagen.'); return;
    }
    setImgBusy(true);
    try {
      const form = new FormData();
      form.append('image', file);
      // multipart: fetch directo con el JWT (mismo patrón que el upload de logo).
      const res = await fetch(`${API_BASE}/communications/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) { setImgErr(data?.message ?? 'No se pudo subir la imagen'); return; }
      exec('insertImage', data.url);
    } catch (e: any) {
      setImgErr(e?.message ?? 'No se pudo subir la imagen');
    } finally { setImgBusy(false); }
  };

  // Pegar imagen del portapapeles (screenshot, copiar/pegar) → mismo upload.
  const onPaste = (e: React.ClipboardEvent) => {
    const file = Array.from(e.clipboardData?.files ?? [])
      .find((f) => f.type.startsWith('image/'));
    if (file) {
      e.preventDefault();
      void uploadImage(file);
    }
  };

  const tb = 'rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50';

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        <button type="button" className={tb} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}><b>B</b></button>
        <button type="button" className={tb} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}><i>I</i></button>
        <button type="button" className={tb} onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}><u>S</u></button>
        <button type="button" className={tb} onMouseDown={(e) => { e.preventDefault(); link(); }}>Enlace</button>
        <button type="button" className={tb} onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>• Lista</button>
        <button type="button" className={tb} disabled={imgBusy}
          onMouseDown={(e) => { e.preventDefault(); fileRef.current?.click(); }}>
          {imgBusy ? 'Subiendo…' : '🖼 Imagen'}
        </button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadImage(f); e.target.value = ''; }} />
      </div>
      {imgErr && <p className="mb-1.5 text-[11px] text-rose-600">{imgErr}</p>}
      <div
        ref={ref}
        contentEditable
        onInput={(e) => onChange((e.target as HTMLDivElement).innerHTML)}
        onPaste={onPaste}
        className="min-h-[180px] rounded-xl border border-slate-200 p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-slate-300 [&_img]:max-w-full [&_img]:rounded-lg"
      />
      <p className="mt-1 text-[11px] text-slate-400">
        Tip: también puedes pegar una imagen directo en el texto (Ctrl+V).
      </p>
    </div>
  );
}

// ===========================================================================
// Paso 2 — selección con elegibilidad + ahora/programado
// ===========================================================================
function RecipientsStep({ draftId, onBack, onDone }: {
  draftId: string; onBack: () => void; onDone: () => void;
}) {
  const [eligible, setEligible] = useState<EligibleContact[]>([]);
  const [suppressed, setSuppressed] = useState<EligibleContact[]>([]);
  const [noEmailCount, setNoEmailCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<'now' | 'later'>('now');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const loadPreview = () =>
    api('/communications/recipients-preview').then((r: any) => {
      setEligible(r.eligible); setSuppressed(r.suppressed); setNoEmailCount(r.noEmailCount);
    }).finally(() => setLoading(false));
  useEffect(() => { loadPreview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onResubscribe = async (email: string) => {
    try { if (await resubscribe(email)) await loadPreview(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo re-suscribir'); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return eligible;
    return eligible.filter((c) =>
      c.name.toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle));
  }, [eligible, q]);

  const allSelected = eligible.length > 0 && checked.size === eligible.length;
  const toggleAll = () =>
    setChecked(allSelected ? new Set() : new Set(eligible.map((c) => c.id)));
  const toggle = (id: string) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    setErr(null);
    if (checked.size === 0) { setErr('Selecciona al menos un destinatario.'); return; }
    if (mode === 'later' && !when) { setErr('Elige fecha y hora de envío.'); return; }
    setBusy(true);
    try {
      await api(`/communications/${draftId}/schedule`, {
        method: 'POST',
        body: {
          ...(allSelected ? { selectAll: true } : { contactIds: Array.from(checked) }),
          ...(mode === 'later' ? { scheduledAt: new Date(when).toISOString() } : {}),
        },
      });
      onDone();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo agendar');
    } finally { setBusy(false); }
  };

  if (loading) return <p className="text-sm text-slate-500">Cargando contactos…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={allSelected} onChange={toggleAll} />
          Seleccionar todos ({eligible.length} elegibles)
        </label>
        <input className="input max-w-[220px]" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {(noEmailCount > 0 || suppressed.length > 0) && (
        <p className="text-[11px] text-slate-400">
          Excluidos: {noEmailCount > 0 && `${noEmailCount} sin correo`}
          {noEmailCount > 0 && suppressed.length > 0 && ' · '}
          {suppressed.length > 0 && `${suppressed.length} dados de baja (no se les puede enviar)`}
        </p>
      )}
      <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-200">
        {filtered.map((c) => (
          <label key={c.id} className="flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm hover:bg-slate-50">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300"
              checked={checked.has(c.id)} onChange={() => toggle(c.id)} />
            <span className="font-medium text-slate-800">{c.name || c.email}</span>
            <span className="ml-auto text-xs text-slate-400">{c.email}</span>
          </label>
        ))}
        {suppressed.map((c) => (
          <div key={c.id} className="flex items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 opacity-45" disabled />
            <span className="text-slate-500 line-through opacity-60">{c.name || c.email}</span>
            <span className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-slate-400">dado de baja</span>
              {/* Re-suscripción por staff: el confirm lleva la advertencia legal
                  y el backend registra la acción en audit_logs. */}
              <button type="button" className="text-[11px] font-medium text-slate-500 underline hover:text-slate-800"
                onClick={() => onResubscribe(c.email)}>
                Re-suscribir
              </button>
            </span>
          </div>
        ))}
        {filtered.length === 0 && suppressed.length === 0 && (
          <p className="px-3 py-4 text-center text-sm text-slate-400">Sin contactos con correo.</p>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === 'now'} onChange={() => setMode('now')} /> Enviar ahora
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === 'later'} onChange={() => setMode('later')} /> Programar:
        </label>
        {mode === 'later' && (
          <input type="datetime-local" className="input max-w-[220px]" value={when}
            onChange={(e) => setWhen(e.target.value)} />
        )}
      </div>

      {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
      <div className="flex justify-between">
        <button className={btnGhost} onClick={onBack}>← Volver al mensaje</button>
        <button className={btnPrimary} disabled={busy} onClick={submit}>
          {busy ? 'Agendando…' : mode === 'now'
            ? `Enviar ahora a ${checked.size}`
            : `Programar para ${checked.size}`}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Bajas — lista de suprimidos + re-suscripción por staff (item follow-up:
// el DELETE con audit ya existía en backend pero sin UI, no se encontraba).
// ===========================================================================
function SuppressionsModal({ onClose }: { onClose: () => void }) {
  const [suppressed, setSuppressed] = useState<EligibleContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    api('/communications/recipients-preview').then((r: any) => setSuppressed(r.suppressed))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onResubscribe = async (email: string) => {
    setErr(null);
    try { if (await resubscribe(email)) await load(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo re-suscribir'); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Bajas de comunicados</h3>
          <button className="text-slate-400 hover:text-slate-600" onClick={onClose}>✕</button>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Contactos que se dieron de baja de los comunicados de tu negocio (sus correos
          de citas/reservas siguen llegando). <b>Solo re-suscribe si el cliente te lo
          pidió expresamente</b> — hacerlo sin su consentimiento es ilegal; cada
          re-suscripción queda registrada en la bitácora.
        </p>
        {err && <p className="alert-over mb-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
        {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            {suppressed.length === 0 && (
              <p className="px-3 py-5 text-center text-sm text-slate-400">
                Nadie se ha dado de baja. 🎉
              </p>
            )}
            {suppressed.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-sm">
                <span>
                  <span className="font-medium text-slate-700">{c.name || c.email}</span>
                  {c.name && <span className="ml-1.5 text-xs text-slate-400">{c.email}</span>}
                </span>
                <button type="button" className={btnGhost} onClick={() => onResubscribe(c.email)}>
                  Re-suscribir
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Detalle (historial por destinatario)
// ===========================================================================
function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [data, setData] = useState<any | null>(null);
  useEffect(() => { api(`/communications/${id}`).then(setData).catch(() => {}); }, [id]);

  const s = data ? (STATUS_META[data.status] ?? { label: data.status, cls: 'bg-slate-100 text-slate-600' }) : null;
  const R_STATUS: Record<string, string> = {
    PENDING: 'pendiente', SENT: 'enviado', FAILED: 'falló',
    SUPPRESSED: 'excluido (baja)', CANCELLED: 'cancelado',
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[88vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        {!data ? <p className="text-sm text-slate-500">Cargando…</p> : (
          <>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-serif text-lg text-slate-900">{data.subject}</h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  <span className={`mr-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${s!.cls}`}>{s!.label}</span>
                  {data.status === 'SENT' && `Enviado ${fmt(data.sentAt)}`}
                  {data.status === 'SCHEDULED' && `Sale ${fmt(data.scheduledAt)}`}
                </p>
              </div>
              <button className="text-slate-400 hover:text-slate-600" onClick={onClose}>✕</button>
            </div>
            <div className="mb-4 rounded-xl border border-slate-200 p-3 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: data.bodyHtml }} />
            {data.recipients?.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="px-3 py-1.5">Destinatario</th><th className="px-3 py-1.5">Estado</th><th className="px-3 py-1.5">Enviado</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.recipients.map((r: any, i: number) => (
                      <tr key={i}>
                        <td className="px-3 py-1.5">{r.name ? `${r.name} · ` : ''}{r.email}</td>
                        <td className="px-3 py-1.5">{R_STATUS[r.status] ?? r.status}{r.error ? ` — ${r.error}` : ''}</td>
                        <td className="px-3 py-1.5 text-slate-500">{fmt(r.sentAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
